import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Loader2, Mail, Lock, Briefcase } from "lucide-react";
import { useAuth, homePathFor } from "@/context/AuthContext";
import { apiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LegalFooter from "@/components/LegalFooter";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = from || homePathFor(u);
      navigate(dest, { replace: true });
    } catch (err) {
      if (err?.code === "CLIENT_ROLE_REJECTED") {
        setError("This is a client account — please use the Client Portal.");
      } else {
        setError(apiError(err, "Invalid email or password."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-white px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <img src="/fdcheck-logo.jpg" alt="FD Check" className="h-24 w-auto object-contain" />
          <p className="mt-2 text-sm text-slate-500">Sign in to your account</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  className="pl-10"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Sign in
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-medium text-slate-400">Property manager?</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Link to="/portal/login">
            <Button variant="outline" className="w-full" data-testid="goto-portal-login">
              <Briefcase className="mr-2 h-4 w-4" /> Client Portal Access
            </Button>
          </Link>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          New here? <Link to="/register" className="font-medium text-primary hover:underline" data-testid="goto-register">Create a company or join one</Link>
        </p>
        <LegalFooter className="mt-3" />
      </div>
    </div>
  );
}
